import { useEffect, useState } from 'react';
import { useAppSelector } from 'redux-state/configure-store.js';
import { WarningLoadingState } from 'redux-state/weather-warnings/types.js';
import { ILocationSummary } from 'types/markerTypesV1/marker-v1-view-type.js';
import { getWarningsForDay } from 'utilities/weather-warnings/warning-intervals.js';
import { getSeverityInfo, WarningSeverity, warnLevelHighest } from 'utilities/weather-warnings/warning-severity.js';
import { getCurrentLocationWarnings } from 'utilities/weather-warnings/warnings.js';
import { isValidDate, IUseWarningsType, IWarningForDays } from './use-warnings-utils.js';

/**
 * This hook returns an array of warnings by date
 * Warnings are often requested based on mulitple day forecasts (tab headers) or single day forecasts (hazard info)
 * This serves both by providing an array of hazards over mulitple time frames.
 *
 * @see {@link{src/utilities/warning-severity.js}}
 *
 * Utility functions are available that allow hazard information to be returned via index or date
 */
export const UseWarningsForForecast = (
  locations: ILocationSummary | undefined = undefined,
): {
  warningsForTheDays: IWarningForDays[];
  setWarningsDates: React.Dispatch<React.SetStateAction<string[]>>;
} => {
  // <InlineWarning warning={warning} />
  /** Get all weather warnings from redux */
  const weatherWarnings = useAppSelector((state) => state.WEATHER_WARNINGS.warnings);
  /** Get location from redux */
  const layoutScreen = useAppSelector((state) => state.LAYOUT.screen);
  const location = locations ?? layoutScreen.location; // props.config.location ||
  /** Date being viewed for warnings */
  const [warningsDates, setWarningsDates] = useState<string[]>([]);
  /** List of warnings for that date */
  const [warningsForTheDays, setWarningsForTheDays] = useState<IWarningForDays[]>([]);

  const [warningState, setWarningState] = useState<IUseWarningsType>({
    warnings: [],
    state: WarningLoadingState.loading,
  });

  /** Load reload page weather warnings on page/weather warning update */
  useEffect(() => {
    const warningState = getCurrentLocationWarnings(location ?? {}, weatherWarnings);
    if (location && warningState) {
      const warnings = warningState.warnings ?? [];
      setWarningState({ warnings, state: WarningLoadingState.loaded });
    } else {
      setWarningState({ warnings: [], state: WarningLoadingState.failed });
    }
  }, [weatherWarnings, layoutScreen]);

  /** Update warnings provided up date update */
  useEffect(() => {
    const warningDataArr = warningsDates.map((warningsDay) => {
      const date = warningsDay ? new Date(warningsDay) : undefined;
      const dateString = warningsDay;
      if (isValidDate(date)) {
        const warnings = getWarningsForDay(date, warningState.warnings);
        const warnLevel = warnLevelHighest(warnings.map(({ warnLevel }) => warnLevel));
        const { colorClass } = getSeverityInfo(warnLevel);
        const result: IWarningForDays = {
          warnings,
          colorClass,
          date,
          dateString,
          severityLevel: getSeverityInfo(warnLevel),
        };
        return result;
      }
      const result: IWarningForDays = {
        warnings: [],
        colorClass: '',
        date: undefined,
        dateString,
        severityLevel: getSeverityInfo(WarningSeverity.None),
      };
      return result;
    });

    setWarningsForTheDays(warningDataArr);
  }, [warningsDates, warningState]);

  return { warningsForTheDays, setWarningsDates };
};
